import { ChangeDetectorRef, Component } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { NavbarComponent } from '../../components/navbars/navbar/navbar.component';
import { SideMenuComponent } from '../../components/navbars/side-menu/side-menu.component';
import { DataService } from '../../Services/Data Service/data.service';
import { AuditService } from '../../Services/Data Service/audit.service';
import { FormsModule } from '@angular/forms';
import { CommonModule, NgFor, NgIf } from '@angular/common';
import { AuthService } from '../../Auth/auth.service';
import { filter, forkJoin, map, of, switchMap, take } from 'rxjs';
import { HttpClientModule } from '@angular/common/http';
import { CacheService } from '../../Services/Data Service/cache.service';


@Component({
  selector: 'app-dashboard',
  standalone: true,
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css',
  imports: [ NgIf, SideMenuComponent,
    NavbarComponent, RouterModule, SideMenuComponent,
    FormsModule, CommonModule, NgFor]
})
export class DashboardComponent {
  chartData: any;
  pendingaccounts: any;
  vendorActionAccounts: any;
  acceptedaccounts: any;
  rejectedaccounts: any;
  flaggedaccounts: any;
  allaccounts: any;
  currUser: any;
  loading = false;
  /** User PK while a flag/unflag request is in flight (dashboard cards). */
  flaggingUserId: number | null = null;

  filteredAccounts: any[] = [];
  activeSystemCount: any;
  inactiveSystemCount: any;
  underReviewCount: any;
  vendorActionRequiredCount: any;

  constructor(
    private dataService: DataService,
    private auditService: AuditService,
    private auth: AuthService, 
    private cdr: ChangeDetectorRef,
    private cacheService: CacheService
  ) { };
  isNavbarExpanded = false;

  toggleCollapse(expanded: boolean) {
    this.isNavbarExpanded = expanded;
  }

  ngOnInit() { 
    this.loadCountsFromCache();

    this.getPending();
    this.getVendorAction();
    this.getAccepted();
    this.getRejected();
    this.getFlagged();
    // this.fetchData();

    this.dataService.currentUser$.pipe(
      filter((u): u is NonNullable<typeof u> => u != null),
      take(1)
    ).subscribe({
      next: (loggedInUser) => {
        if (!loggedInUser.role) {
          console.error('User not found.');
          this.loading = false;
          return;
        }
        this.currUser = loggedInUser;
      }
    });
  
  };

  value: any;
  siteid: any;
  start: any = "";
  victronChartData: any;
  selectedSystem: any;
  siteData: any;
  system: any;
  currentUser: any;
  companies: any = [];
  
 private fetchAccountsByStatus(
    status: string, 
    targetProperty: 'pendingaccounts' | 'vendorActionAccounts' | 'acceptedaccounts' | 'rejectedaccounts',
    loadingKey: keyof typeof this.loadingStates,
    forceRefresh = false 
  ) {
    this.loadingStates[loadingKey] = true;

    const cacheKey = `accounts_${status.replace(/\s+/g, '_').toLowerCase()}`; // e.g., "accounts_under_review"
    const cached = this.cacheService.get<any[]>(cacheKey);

    if (cached && !forceRefresh) {
      this[targetProperty] = cached;
      this.loadingStates[loadingKey] = false;
      this.cdr.detectChanges();

      // Background refresh
      this.refreshAccountsByStatus(status, targetProperty, loadingKey, cacheKey);
      return;
    }

    // If forceRefresh OR no cache → fetch fresh
    this.refreshAccountsByStatus(status, targetProperty, loadingKey, cacheKey);
  }

  // New helper for refresh + caching
  private refreshAccountsByStatus(
    status: string, 
    targetProperty: 'pendingaccounts' | 'vendorActionAccounts' | 'acceptedaccounts' | 'rejectedaccounts',
    loadingKey: keyof typeof this.loadingStates,
    cacheKey: string
  ) {
    this.dataService.getUsers().pipe(
      map((result: any[]) => {
        const filtered = result
          .filter(user => user.strAccountStatus === status)
          .filter((item: any) => item.role === 'Vendor') //vendor accounts only
          .sort((a, b) => new Date(b.strAccountStatusDate).getTime() - new Date(a.strAccountStatusDate).getTime());
        return filtered;
      }),
      switchMap((users: any[]) => {
        if (!users.length) {
          return of([]); // short-circuit: return empty array as observable
        }

        const usersWithCompany$ = users.map(user =>
          this.dataService.getCompanyByPK(user.company.intCompanyPK).pipe(
            map(company => ({ ...user, company }))
          )
        );
        return forkJoin(usersWithCompany$);
      })
    ).subscribe({
      next: (usersWithCompany) => {
        this[targetProperty] = usersWithCompany;

        // Cache fresh results for 5 minutes
        this.cacheService.set(cacheKey, usersWithCompany, 1000 * 60 * 5);
        this.updateActiveSystemCount();
        this.cdr.detectChanges();
        this.loadingStates[loadingKey] = false;
      },
      error: (error) => {
        this.loadingStates[loadingKey] = false;
        console.error(`[${status}] Error fetching users or companies:`, error);
      }
    });
  }


  loadingStates = {
    pending: false,
    vendorAction: false,
    approved: false,
    declined: false
  };
  
  getPending(forceRefresh = false) {
    this.pendingaccounts = []; //reset to avoid stale data persisting
    this.fetchAccountsByStatus('Under Review', 'pendingaccounts', 'pending', forceRefresh);
  }

  getVendorAction(forceRefresh = false) {
    this.vendorActionAccounts = []; //reset to avoid stale data persisting
    this.fetchAccountsByStatus('Vendor Action Required', 'vendorActionAccounts', 'vendorAction', forceRefresh);
  }
  
  getAccepted() {
    this.acceptedaccounts = [];
    this.fetchAccountsByStatus('Approved', 'acceptedaccounts', 'approved');
  }
  
  getRejected() {
    this.rejectedaccounts = [];
    this.fetchAccountsByStatus('Declined', 'rejectedaccounts', 'declined');
  }
  
  getFlagged(forceRefresh = false) {
    this.flaggedaccounts = [];
    this.loading = true;

    const cacheKey = 'accounts_flagged';
    const cached = this.cacheService.get<any[]>(cacheKey);

    if (cached && !forceRefresh) {
      this.flaggedaccounts = cached;
      this.loading = false;
      this.cdr.detectChanges();

      // Background refresh
      this.refreshFlagged(cacheKey);
      return;
    }

    // If forceRefresh OR no cache → fetch fresh
    this.refreshFlagged(cacheKey);
  }

  private refreshFlagged(cacheKey: string) {
    this.dataService.getUsers().pipe(
      map((result: any[]) => result.filter(user => user.blnFlagged === true))
    ).subscribe({
      next: (flaggedUsers) => {
        this.flaggedaccounts = flaggedUsers;
        this.cacheService.set(cacheKey, flaggedUsers, 1000 * 60 * 5); // cache for 5 min
        this.cdr.detectChanges();
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        console.error(`Error fetching flagged users:`, error);
      }
    });
  }

  openViewForm(userId: number) {
    window.location.href= `/registrationform/${userId}`;
  }

  isFlagUpdating(account: { intUserPK: number }): boolean {
    return this.flaggingUserId === account.intUserPK;
  }

  /** Keep list cache in sync without refetching the whole pending section. */
  private patchFlagInListCache(cacheKey: string, account: any, flagged: boolean): void {
    const cached = this.cacheService.get<any[]>(cacheKey);
    if (!cached?.length) {
      return;
    }
    const updated = cached.map((u) =>
      u.intUserPK === account.intUserPK ? { ...u, blnFlagged: flagged } : u
    );
    this.cacheService.set(cacheKey, updated, 1000 * 60 * 5);
  }

  flagForFollowUp(account: any) {
    if (this.isFlagUpdating(account)) {
      return;
    }
    this.flaggingUserId = account.intUserPK;
    this.auditService.submitAuditLog("Flagged vendor: " + account.strName);

    this.dataService.updateFlagged(account.intUserPK, true).subscribe({
      next: () => {
        account.blnFlagged = true;
        this.patchFlagInListCache('accounts_under_review', account, true);

        this.cdr.detectChanges();
        this.flaggingUserId = null;
      },
      error: (err) => {
        console.error('[flagForFollowUp] Failed to update status', err);
        this.flaggingUserId = null;
      }
    });
  }

  unflagAccount(account: any) {
    if (this.isFlagUpdating(account)) {
      return;
    }
    this.flaggingUserId = account.intUserPK;
    this.auditService.submitAuditLog("Unflagged vendor: " + account.strName);

    this.dataService.updateFlagged(account.intUserPK, false).subscribe({
      next: () => {
        account.blnFlagged = false;
        this.patchFlagInListCache('accounts_under_review', account, false);

        this.cdr.detectChanges();
        this.flaggingUserId = null;
      },
      error: (err) => {
        console.error('[unflagAccount] Failed to update status', err);
        this.flaggingUserId = null;
      }
    });
  }

  
  formatSubmissionDate(dateString: string): string {
    const submittedDate = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - submittedDate.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
    let timeAgoText = 'today';
    if (diffDays === 1) {
      timeAgoText = '1 day ago';
    } else if (diffDays > 1) {
      timeAgoText = `${diffDays} days ago`;
    }
  
    const formattedDate = submittedDate.toLocaleDateString('en-US'); // e.g., 04/01/2025
    return `Submitted ${timeAgoText} (${formattedDate})`;
  }
  
    // FOR ACCOUNT COUNTS
    updateActiveSystemCount(): void {
      // Merge all arrays
      const mergedAccounts = [
        ...(this.pendingaccounts || []),
        ...(this.vendorActionAccounts || []),
        ...(this.acceptedaccounts || []),
        ...(this.rejectedaccounts || []),
        ...(this.flaggedaccounts || [])
      ];

      // Remove duplicates by user ID
      const uniqueAccountsMap = new Map<number, any>();
      mergedAccounts.forEach(acc => uniqueAccountsMap.set(acc.intUserPK, acc));
      const allAccounts = Array.from(uniqueAccountsMap.values());

      this.activeSystemCount = allAccounts.filter((record: any) => record.strAccountStatus === 'Approved').length;
      this.inactiveSystemCount = allAccounts.filter((record: any) => record.strAccountStatus === 'Declined').length;
      this.underReviewCount = allAccounts.filter((record: any) => record.strAccountStatus === 'Under Review').length;
      this.vendorActionRequiredCount = allAccounts.filter((record: any) => record.strAccountStatus === 'Vendor Action Required').length;

      // Cache counts
      this.cacheService.set('dashboardCounts', {
        active: this.activeSystemCount,
        inactive: this.inactiveSystemCount,
        underReview: this.underReviewCount,
        vendorActionRequired: this.vendorActionRequiredCount
      }, 1000 * 60 * 5);
    }

    // New helper: read counts from cache on init
    loadCountsFromCache(): void {
      const cachedCounts = this.cacheService.get<{active:number, inactive:number, underReview:number, vendorActionRequired:number}>('dashboardCounts');
      if (cachedCounts) {
        this.activeSystemCount = cachedCounts.active;
        this.inactiveSystemCount = cachedCounts.inactive;
        this.underReviewCount = cachedCounts.underReview;
        this.vendorActionRequiredCount = cachedCounts.vendorActionRequired;
        this.cdr.detectChanges();
      } else {
        // If nothing cached, compute fresh
        this.updateActiveSystemCount();
      }
    }

    // fetchData(): void {
    //   this.dataService.getUsers().subscribe({
    //     next: (result) => {
    //       //VENDOR ACCOUNT ONLY
    //       const vendorAccounts = result.filter((item: any) => item.role === 'Vendor');
  
    //       this.filteredAccounts = vendorAccounts;
    //       this.updateActiveSystemCount();
    //     }
    //   })

    // }
    
}